// The one place a catalogue price becomes text or a pixel value —
// docs/plans/ab-testing.md D6, CS7.
//
// A price test's DISPLAY is shared and client-applied: running price and
// shipping tests ride /bootstrap, the catalogue responses stay list price (they
// are edge-cached for everyone, so a per-shopper price there would be served to
// the next shopper), and this module applies the visit's arm. The CHARGE is the
// server's, from the same tables, for a cart that acknowledged the test (the
// X-DK-Exp header lib/api.ts sends on cart calls).
//
// Which arm: the CART's, once the cart carries one — it is priced under the
// session that created it, for its whole life, including after the test ends —
// else this session's arm of every running test. A preview renders control
// unless the link forces an arm (visitArm).
//
// test/display-price-guard.test.ts fails on any raw variant `.price_cents` read
// in apps/storefront/src or packages/bundle-ui/src outside this file and the
// server-priced surfaces it names — so a new price display cannot skip the arm.

import { getActivePinia } from "pinia"
import { cartFreeShipThreshold, type CartSettings } from "@dk/theme"
import { visitArm, type PublicExperiment } from "./experiment"

type Dict = Record<string, unknown>

/** One price arm as /bootstrap or the cart carries it. */
interface PriceArm {
  prices?: Record<string, number>
  /** product handle → the cheapest variant under this arm, for a product card */
  from?: Record<string, number>
}

/** A money arm on the cart response (experiment.CartArm in Go). */
export interface CartMoneyArm {
  key: string
  kind: string
  arm: number
  prices?: Record<string, number>
  threshold_cents?: number
}

/** The bootstrap and cart stores' state, read through Pinia's own registry
 *  rather than by importing the stores: this module is reached from the
 *  tracking pipeline, whose tests run where there is no `location` for the
 *  cart store to read at import. The state is reactive, so a render reading a
 *  price here re-renders when the cart's arm arrives. No active Pinia (a unit
 *  test, or code before the app mounted) is list price. */
function sources(): { exps: PublicExperiment[]; cartArms: CartMoneyArm[] } {
  const state = getActivePinia()?.state.value as
    | {
        bootstrap?: { payload?: { experiments?: PublicExperiment[] } | null }
        cart?: { cart?: { experiments?: CartMoneyArm[] } | null; arms?: CartMoneyArm[] }
      }
    | undefined
  const cart = state?.cart
  return {
    exps: state?.bootstrap?.payload?.experiments ?? [],
    // Before GET /cart answers, the arms the store remembered beside the token.
    cartArms: (cart?.cart ? cart.cart.experiments : cart?.arms) ?? [],
  }
}

/** The arms of one kind this visit displays, cart first: `fromCart` reads a
 *  cart arm (with the running test, when there is one), `fromPayload` a running
 *  test's served arm for this session. Undefined means "control here". */
function arms<T>(
  kind: string,
  fromCart: (a: CartMoneyArm, running?: PublicExperiment) => T | undefined,
  fromPayload: (payload: unknown) => T | undefined,
): T[] {
  const { exps, cartArms } = sources()
  const out: T[] = []
  const decided = new Set<string>()
  for (const a of cartArms) {
    if (a.kind !== kind) continue
    decided.add(a.key)
    const t = fromCart(a, exps.find((e) => e.key === a.key))
    if (t !== undefined) out.push(t)
  }
  for (const e of exps) {
    if (e.kind !== kind || decided.has(e.key)) continue
    const i = visitArm(e)
    const t = i > 0 ? fromPayload(e.payloads[i]) : undefined
    if (t !== undefined) out.push(t)
  }
  return out
}

function priceArms(): PriceArm[] {
  // A cart in arm 0 is control for that test: nothing to apply, and the
  // session's own arm must not be applied either. A running test's served arm
  // carries the card "from" prices the cart response does not.
  return arms<PriceArm>(
    "price",
    (a, running) => (a.arm > 0 ? ((running?.payloads[a.arm] as PriceArm | undefined) ?? { prices: a.prices }) : undefined),
    (p) => p as PriceArm,
  )
}

/** A variant's LIST price — for structured data only. JSON-LD is control, as
 *  the bot prerender is (plan D6's table): a crawler has no session, and an
 *  indexed arm price would be a price most shoppers are never offered. */
export function listPrice(v: { price_cents: number }): number {
  return v.price_cents
}

/** A variant's displayed price, in cents. */
export function displayPrice(v: { id: number; price_cents: number }): number {
  for (const a of priceArms()) {
    const p = a.prices?.[String(v.id)]
    if (typeof p === "number") return p
  }
  return v.price_cents
}

/** The cheapest displayed price among a product's variants, or null for none —
 *  what a featured-product section shows as "from". Call it at render, not at
 *  fetch time: the cart's arm can arrive after the product did. */
export function displayMin(vs: readonly { id: number; price_cents: number }[] | undefined): number | null {
  if (!vs?.length) return null
  return Math.min(...vs.map(displayPrice))
}

/** A product card's displayed "from" price, in cents — the cheapest variant
 *  under the visit's arm, by the card's handle (a card carries no variants).
 *  Undefined when the card carries no price. */
export function displayCardPrice(p: { handle: string; price_cents?: number }): number | undefined {
  if (p.price_cents == null) return undefined
  for (const a of priceArms()) {
    const f = a.from?.[p.handle]
    if (typeof f === "number") return f
  }
  return p.price_cents
}

/** The free-shipping threshold the drawer's bar shows, in MAJOR units like
 *  cartFreeShipThreshold (0 = off): a shipping arm's own, else the theme's. */
export function displayFreeShipThreshold(block: Dict | undefined, legacy?: CartSettings): number {
  const [cents] = arms<number>(
    "shipping",
    (a) => (a.arm > 0 ? a.threshold_cents : undefined),
    (p) => (p as { threshold_cents?: number } | undefined)?.threshold_cents,
  )
  return typeof cents === "number" ? cents / 100 : cartFreeShipThreshold(block, legacy)
}
